import { useState, type FormEvent, type ReactElement } from "react";
import { Modal } from "@/components/ui/Modal";
import type { Broker, StrategyCreateRequest, StrategyType } from "@/types/strategy";

interface Props {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (req: StrategyCreateRequest) => void;
}

/** The venue each type is designed to run on. Kite: webhook bias + the
 * BB bottom-buy engine (its own broker). Fyers: the 5/50 EMA trend engine — a
 * CAPACITY decision rather than a data one, since three strategies already
 * sit on Kite and each engine polls its own spot every tick. Picked as the
 * default on type change; the operator can still override before creating. */
const FYERS_TYPES: readonly StrategyType[] = ["ema_trend"];
/** Failed Breakout reads Dhan's prices and candles and trades through Dhan (docs/dhan.md). */
const DHAN_TYPES: readonly StrategyType[] = ["failed_breakout", "stpivot_nifty", "stpivot_sensex"];

function defaultBrokerFor(type: StrategyType): Broker {
  if (DHAN_TYPES.includes(type)) return "dhan";
  return FYERS_TYPES.includes(type) ? "fyers" : "kite";
}

/** A new Failed Breakout, STPIVOT or Manual trading strategy starts in PAPER: real money only
 * after the paper phase. */
function defaultModeFor(type: StrategyType): "PAPER" | undefined {
  return DHAN_TYPES.includes(type) || type === "manual" ? "PAPER" : undefined;
}

export function CreateStrategyDialog({
  open,
  busy,
  onCancel,
  onSubmit,
}: Readonly<Props>): ReactElement {
  const f = useCreateForm(onSubmit, onCancel);
  const disabled = busy || f.name.trim().length === 0;
  return (
    <Modal
      open={open}
      onClose={f.close}
      title="Create strategy"
      description="Webhook URL and access token are shown once. Save the token immediately."
      icon="ti-plus"
      iconTone="accent"
      footer={<Footer busy={busy} disabled={disabled} onClose={f.close} />}
    >
      <CreateForm
        name={f.name}
        type={f.type}
        broker={f.broker}
        onName={f.setName}
        onType={f.pickType}
        onBroker={f.setBroker}
        onSubmit={f.handleSubmit}
      />
    </Modal>
  );
}

function useCreateForm(onSubmit: Props["onSubmit"], onCancel: () => void) {
  const [name, setName] = useState("");
  const [type, setType] = useState<StrategyType>("ema_trend");
  const [broker, setBroker] = useState<Broker>("fyers");
  const reset = (): void => {
    setName("");
    setType("ema_trend");
    setBroker("fyers");
  };
  return {
    name,
    type,
    broker,
    setName,
    setBroker,
    // Type drives the sensible broker default, PER TYPE.
    pickType: (next: StrategyType): void => {
      setType(next);
      setBroker(defaultBrokerFor(next));
    },
    close: (): void => {
      reset();
      onCancel();
    },
    handleSubmit: (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      const trimmed = name.trim();
      if (trimmed.length === 0) return;
      const mode = defaultModeFor(type);
      onSubmit({
        name: trimmed,
        broker,
        strategy_type: type,
        ...(mode === undefined ? {} : { execution_mode: mode }),
      });
      reset();
    },
  };
}

interface FooterProps {
  busy: boolean;
  disabled: boolean;
  onClose: () => void;
}

function Footer({ busy, disabled, onClose }: Readonly<FooterProps>): ReactElement {
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button
        type="submit"
        form="create-strategy-form"
        className="btn btn-primary"
        disabled={disabled}
      >
        {busy ? "Creating..." : "Create"}
      </button>
    </>
  );
}

interface CreateFormProps {
  name: string;
  type: StrategyType;
  broker: Broker;
  onName: (s: string) => void;
  onType: (t: StrategyType) => void;
  onBroker: (b: Broker) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

// bias and bb_bottom_buy are RETIRED (2026-08-28) and deliberately absent:
// their historical strategies still render, but no new one can be created
// because no engine would run it.
const TYPE_OPTIONS: readonly (readonly [string, string])[] = [
  // Fyers, not Kite — a capacity decision, see ema_trend_executor.
  ["ema_trend", "5/50 EMA Trend (auto)"],
  // Kite: its 15:24 square-off is the rule's (docs/poi1.md §10).
  ["poi1", "POI-1 Opening Candle (auto)"],
  // Dhan: its own prices and candles; created in PAPER (docs/failed_breakout.md).
  ["failed_breakout", "Failed Breakout (auto, PAPER first)"],
  // Dhan: SuperTrend + pivot R1/S1, one strategy per index (docs/stpivot.md).
  ["stpivot_nifty", "STPIVOT NIFTY (auto, PAPER first)"],
  ["stpivot_sensex", "STPIVOT SENSEX (auto, PAPER first)"],
  // Trades only on the chart's request; create one on Zerodha and one on Fyers (docs/manual_trading.md).
  ["manual", "Manual trading (from the chart, PAPER first)"],
];

const BROKER_OPTIONS: readonly (readonly [string, string])[] = [
  ["kite", "Zerodha"],
  ["fyers", "Fyers"],
  ["dhan", "Dhan"],
];

function CreateForm(props: Readonly<CreateFormProps>): ReactElement {
  return (
    <form id="create-strategy-form" onSubmit={props.onSubmit} className="space-y-3">
      <label className="block">
        <span className="label">Name</span>
        <input
          className="input"
          value={props.name}
          onChange={(e) => {
            props.onName(e.target.value);
          }}
          placeholder="e.g. EMA Trend, POI-1"
          autoFocus
          maxLength={80}
          required
        />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <SelectField
          label="Type"
          value={props.type}
          options={TYPE_OPTIONS}
          onChange={(v) => {
            props.onType(v as StrategyType);
          }}
        />
        <SelectField
          label="Broker"
          value={props.broker}
          options={BROKER_OPTIONS}
          onChange={(v) => {
            props.onBroker(v as Broker);
          }}
        />
      </div>
      <p className="text-xs text-ink-secondary">
        Type &amp; broker are fixed once created. EMA Trend runs its own signal engine and orders on
        Fyers; POI-1 runs on Zerodha. Trading window and dedup can be tuned later.
      </p>
    </form>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  options: readonly (readonly [string, string])[];
  onChange: (v: string) => void;
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: Readonly<SelectFieldProps>): ReactElement {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <select
        className="input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        {options.map(([val, text]) => (
          <option key={val} value={val}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}
